import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BookOpen, Users, Award, BarChart3, Building2, DollarSign,
  ChevronRight, AlertCircle, CheckCircle, Settings, RefreshCw,
  Eye, Edit2, ToggleLeft, ToggleRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'courses', label: 'Cursos', icon: BookOpen },
  { id: 'alumnos', label: 'Alumnos', icon: Users },
  { id: 'cohortes', label: 'Cohortes', icon: Building2 },
  { id: 'waca', label: 'Reparto WACA', icon: DollarSign },
  { id: 'alerts', label: 'Alertas', icon: AlertCircle },
];

const LINE_COLORS = {
  kids:'bg-purple-500/20 text-purple-400', teens:'bg-blue-500/20 text-blue-400',
  adultos:'bg-emerald-500/20 text-emerald-400', profesionales:'bg-amber-500/20 text-amber-400',
  social:'bg-rose-500/20 text-rose-400', mediadores:'bg-violet-500/20 text-violet-400',
};

// ── Dashboard Tab ─────────────────────────────────────────────────────────
function DashboardTab({ stats, authFetch }) {
  if (!stats) return <div className="py-20 text-center text-[#94A3B8]">Cargando estadísticas...</div>;
  const cards = [
    { label: 'Cursos publicados', value: stats.total_courses, color: 'text-indigo-400', icon: BookOpen },
    { label: 'Inscripciones', value: stats.total_enrollments, color: 'text-emerald-400', icon: Users },
    { label: 'Certificados', value: stats.total_certificates, color: 'text-amber-400', icon: Award },
    { label: 'Cohortes activas', value: stats.total_cohortes, color: 'text-blue-400', icon: Building2 },
    { label: 'Alertas pendientes', value: stats.pending_alerts, color: stats.pending_alerts > 0 ? 'text-red-400' : 'text-[#94A3B8]', icon: AlertCircle },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-[rgba(8,15,32,0.7)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-4 text-center">
            <c.icon size={20} className={`${c.color} mx-auto mb-2`} />
            <p className={`text-3xl font-black ${c.color}`}>{c.value}</p>
            <p className="text-[10px] text-[#94A3B8] mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-5">
        <h3 className="text-sm font-bold text-white mb-3">Accesos rápidos</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/academy" target="_blank" className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition">
            <ChevronRight size={13} /> Ver catálogo público
          </Link>
          <Link to="/academy/mi-perfil" className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition">
            <ChevronRight size={13} /> Panel alumno demo
          </Link>
          <Link to="/acceso-academy" className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition">
            <ChevronRight size={13} /> Login Academy
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Courses Tab ───────────────────────────────────────────────────────────
function CoursesTab({ authFetch }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editStatus, setEditStatus] = useState('');

  useEffect(() => {
    authFetch(`${API}/academy/admin/courses`).then(r => r.json()).then(d => {
      setCourses(d.courses || []); setLoading(false);
    });
  }, [authFetch]);

  const saveEdit = async (courseId) => {
    await authFetch(`${API}/academy/admin/courses/${courseId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ price_usd: parseFloat(editPrice), status: editStatus })
    });
    setCourses(p => p.map(c => c.id === courseId ? { ...c, price_usd: parseFloat(editPrice), status: editStatus } : c));
    setEditing(null);
  };

  if (loading) return <div className="py-10 text-center text-[#94A3B8]">Cargando cursos...</div>;

  return (
    <div className="space-y-2" data-testid="academy-admin-courses">
      {courses.map(c => (
        <div key={c.id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-4">
          <div className="flex items-center gap-4">
            <span className={`text-xs px-2 py-1 rounded-lg font-bold ${LINE_COLORS[c.line] || 'bg-white/10 text-white'}`}>{c.code}</span>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{c.name}</p>
              <p className="text-xs text-[#94A3B8]">{c.total_enrollments} inscritos · {c.total_certificates} certs · {c.weeks}sem</p>
            </div>
            {editing === c.id ? (
              <div className="flex items-center gap-2">
                <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} className="w-20 bg-white/5 border border-[rgba(148,163,184,0.2)] rounded-lg px-2 py-1 text-white text-xs" />
                <select value={editStatus} onChange={e => setEditStatus(e.target.value)} className="bg-[rgba(8,15,32,0.9)] border border-[rgba(148,163,184,0.2)] rounded-lg px-2 py-1 text-white text-xs">
                  <option value="published">Publicado</option>
                  <option value="draft">Borrador</option>
                </select>
                <button onClick={() => saveEdit(c.id)} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1 rounded-lg">Guardar</button>
                <button onClick={() => setEditing(null)} className="text-xs text-[#94A3B8] hover:text-white">✕</button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-white font-bold text-sm">${c.price_usd}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{c.status}</span>
                <button onClick={() => { setEditing(c.id); setEditPrice(c.price_usd); setEditStatus(c.status); }}
                  className="text-[#94A3B8] hover:text-white transition p-1">
                  <Edit2 size={13} />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Alumnos Tab ───────────────────────────────────────────────────────────
function AlumnosTab({ authFetch }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API}/academy/admin/enrollments?limit=50`).then(r => r.json()).then(d => {
      setEnrollments(d.enrollments || []); setLoading(false);
    });
  }, [authFetch]);

  if (loading) return <div className="py-10 text-center text-[#94A3B8]">Cargando inscripciones...</div>;

  return (
    <div className="space-y-2" data-testid="academy-admin-alumnos">
      <p className="text-xs text-[#94A3B8] mb-3">Total: {enrollments.length} inscripciones</p>
      {enrollments.map(e => (
        <div key={e.enrollment_id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-xl px-4 py-3 flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-indigo-600/20 flex items-center justify-center flex-shrink-0">
            <Users size={13} className="text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{e.user_nombre}</p>
            <p className="text-[#94A3B8] text-xs truncate">{e.user_email} · {e.course_name}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 bg-white/10 rounded-full">
                <div className="h-1.5 bg-indigo-500 rounded-full" style={{ width: `${e.progress_pct}%` }} />
              </div>
              <span className="text-xs text-[#94A3B8]">{e.progress_pct}%</span>
            </div>
          </div>
        </div>
      ))}
      {enrollments.length === 0 && <p className="text-center py-10 text-[#94A3B8]">Sin inscripciones</p>}
    </div>
  );
}

// ── Cohortes Tab ──────────────────────────────────────────────────────────
function CohortesTab({ authFetch }) {
  const [cohortes, setCohortes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newData, setNewData] = useState({ name: '', code: '', organization: '', start_date: '', end_date: '', mentor_email: '', safeguard_email: '' });

  useEffect(() => {
    authFetch(`${API}/academy/admin/cohortes`).then(r => r.json()).then(d => {
      setCohortes(d.cohortes || []); setLoading(false);
    });
  }, [authFetch]);

  const createCohorte = async () => {
    const res = await authFetch(`${API}/academy/cohortes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newData)
    });
    const d = await res.json();
    if (d.success) { setCohortes(p => [...p, { ...d.cohorte, member_count: 0 }]); setShowNew(false); }
  };

  if (loading) return <div className="py-10 text-center text-[#94A3B8]">Cargando cohortes...</div>;

  return (
    <div className="space-y-4" data-testid="academy-admin-cohortes">
      <button onClick={() => setShowNew(p => !p)} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition">
        + Nueva cohorte
      </button>
      {showNew && (
        <div className="bg-[rgba(8,15,32,0.9)] border border-indigo-500/30 rounded-2xl p-5 space-y-3">
          <p className="text-sm font-bold text-white">Nueva cohorte</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[['name','Nombre'],['code','Código'],['organization','Organización'],['mentor_email','Email mentor'],['safeguard_email','Email responsable protección'],['start_date','Fecha inicio'],['end_date','Fecha fin']].map(([k,l]) => (
              <div key={k}>
                <label className="text-[10px] text-[#94A3B8] block mb-1">{l}</label>
                <input value={newData[k]} onChange={e => setNewData(p => ({...p,[k]:e.target.value}))}
                  className="w-full bg-white/5 border border-[rgba(148,163,184,0.14)] rounded-xl px-3 py-2 text-white text-xs focus:outline-none focus:border-indigo-500" />
              </div>
            ))}
          </div>
          <button onClick={createCohorte} className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold">Crear cohorte</button>
        </div>
      )}
      {cohortes.map(c => (
        <div key={c.id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs bg-indigo-600/20 text-indigo-400 px-2 py-0.5 rounded-lg">{c.code}</span>
                <p className="text-white font-semibold text-sm">{c.name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{c.status}</span>
              </div>
              <p className="text-[#94A3B8] text-xs mt-1">{c.organization} · {c.start_date} – {c.end_date}</p>
              <p className="text-[#94A3B8] text-xs">Mentor: {c.mentor_email} · Protección: {c.safeguard_email}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white font-bold text-lg">{c.member_count || c.current_participants || 0}</p>
              <p className="text-[10px] text-[#94A3B8]">/ {c.max_participants} cupos</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── WACA Reparto Tab ──────────────────────────────────────────────────────
function WacaRepartoTab({ authFetch }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    authFetch(`${API}/academy/admin/waca-reparto`).then(r => r.json()).then(d => setData(d));
  }, [authFetch]);

  if (!data) return <div className="py-10 text-center text-[#94A3B8]">Calculando...</div>;

  return (
    <div className="space-y-4" data-testid="academy-admin-waca">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 text-center">
        <p className="text-[#94A3B8] text-xs mb-1">Total estimado para WACA (período actual)</p>
        <p className="text-4xl font-black text-amber-400">${data.total_waca_share} USD</p>
      </div>
      <div className="space-y-2">
        {data.courses?.map(c => (
          <div key={c.course_id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-xl p-4 flex items-center gap-4">
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-lg font-bold">{c.course_id}</span>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{c.course_name}</p>
              <p className="text-[#94A3B8] text-xs">{c.enrollments} inscritos × ${c.price_usd} = ${c.revenue_usd}</p>
            </div>
            <div className="text-right">
              <p className="text-amber-400 font-bold text-sm">${c.waca_share_usd}</p>
              <p className="text-[10px] text-[#94A3B8]">{c.waca_pct}% reparto</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#94A3B8] text-center">Estimación basada en inscripciones actuales. El acuerdo final de reparto con WACA requiere firma del MOU complementario.</p>
    </div>
  );
}

// ── Alerts Tab ────────────────────────────────────────────────────────────
function AlertsTab({ authFetch }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API}/academy/alerts`).then(r => r.json()).then(d => {
      setAlerts(d.alerts || []); setLoading(false);
    }).catch(() => setLoading(false));
  }, [authFetch]);

  const markReviewed = async (id) => {
    await authFetch(`${API}/academy/alerts/${id}/review`, { method: 'POST' });
    setAlerts(p => p.filter(a => a.id !== id));
  };

  if (loading) return <div className="py-10 text-center text-[#94A3B8]">Cargando alertas...</div>;

  return (
    <div className="space-y-3" data-testid="academy-admin-alerts">
      {alerts.length === 0 ? (
        <div className="py-16 text-center">
          <CheckCircle size={36} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-medium">Sin alertas pendientes</p>
          <p className="text-[#94A3B8] text-sm">Todas las alertas han sido revisadas</p>
        </div>
      ) : alerts.map(a => (
        <div key={a.id} className={`border rounded-2xl p-4 ${a.type === 'diary_risk' ? 'border-red-500/40 bg-red-500/10' : 'border-amber-500/40 bg-amber-500/10'}`}
          data-testid={`alert-${a.type}`}>
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className={a.type === 'diary_risk' ? 'text-red-400' : 'text-amber-400'} />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">
                {a.type === 'diary_risk' ? '🔴 Alerta de diario — revisión humana urgente' : '🟡 Botón de ayuda pulsado'}
              </p>
              <p className="text-[#94A3B8] text-xs mt-1">{a.message}</p>
              <p className="text-[10px] text-[#94A3B8]/60 mt-1">{a.created_at?.slice(0,16).replace('T',' ')}</p>
            </div>
            <button onClick={() => markReviewed(a.id)} className="text-xs text-white bg-emerald-600/80 hover:bg-emerald-600 px-3 py-1 rounded-lg transition flex-shrink-0">
              Revisado
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────
export default function AcademyAdminPage() {
  const { user, authFetch, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['admin', 'admin_neuralia'].includes(user.role)) {
      navigate('/admin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    authFetch(`${API}/academy/admin/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, [user, authFetch]);

  if (!user) return null;

  const ActiveTab = { dashboard: DashboardTab, courses: CoursesTab, alumnos: AlumnosTab, cohortes: CohortesTab, waca: WacaRepartoTab, alerts: AlertsTab }[tab];

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="academy-admin-page">
      {/* Header */}
      <div className="bg-[rgba(8,15,32,0.98)] border-b border-[rgba(148,163,184,0.14)] px-4 py-3 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#94A3B8] hover:text-white text-sm transition">← Admin</Link>
            <div className="w-px h-5 bg-white/10" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-xl bg-indigo-600 flex items-center justify-center">
                <BookOpen size={13} className="text-white" />
              </div>
              <span className="text-white font-bold text-sm">Academy Admin</span>
            </div>
          </div>
          {stats?.pending_alerts > 0 && (
            <div className="flex items-center gap-1.5 text-red-400 text-xs font-medium animate-pulse">
              <AlertCircle size={13} /> {stats.pending_alerts} alerta{stats.pending_alerts > 1 ? 's' : ''} pendiente{stats.pending_alerts > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className="w-48 flex-shrink-0 space-y-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-xs font-medium transition ${tab === t.id ? 'bg-indigo-600 text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'}`}>
              <t.icon size={14} />
              {t.label}
              {t.id === 'alerts' && stats?.pending_alerts > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{stats.pending_alerts}</span>
              )}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white mb-5 capitalize">{TABS.find(t => t.id === tab)?.label}</h2>
          <ActiveTab stats={stats} authFetch={authFetch} />
        </main>
      </div>
    </div>
  );
}
